import React, { useEffect, useMemo, useState } from 'react';
import usePublicPageScroll from '../../pages/Public/usePublicPageScroll';
import './Auth.css';

const TenantSelector = ({ tenants = [], onSelect, onLogout, loading = false, error = '' }) => {
  /* RELEASE THE SHELL SCROLL LOCK. index.css pins the document
     (html, body, #root { height: 100% } + body { overflow: hidden }) so the
     signed-in layout can scroll its own pane. This page renders inside that
     same #root with no shell, so the lock truncated it at one viewport: with
     eight accounts the Continue button sat below the fold and could not be
     reached at all. Exactly the bug usePublicPageScroll was written for on the
     onboarding pages — same cause, same fix, and it is removed on unmount so
     the signed-in shell is untouched.

     The list ALSO scrolls inside its own box (see .tenant-options--scroll), so
     Continue stays on screen rather than merely becoming reachable. Both are
     wanted: the inner scroll keeps the action visible, the released document
     scroll means a short window can still reach the whole card. */
  usePublicPageScroll();

  const [selectedTenantId, setSelectedTenantId] = useState('');
  const [localError, setLocalError] = useState('');

  const showError = useMemo(() => {
    if (error) return error;
    if (localError) return localError;
    if (!tenants.length) {
      return 'No accounts are assigned to you. Please contact an admin.';
    }
    return '';
  }, [error, localError, tenants]);

  useEffect(() => {
    setSelectedTenantId('');
  }, [tenants]);

  const handleSubmit = (e) => {
    e.preventDefault();
    setLocalError('');

    if (!selectedTenantId) {
      setLocalError('Select an account to continue.');
      return;
    }
    onSelect?.(selectedTenantId);
  };

  return (
    <div className="auth-page">
      <div className="auth-hero">
        <div className="hero-top">
          <div className="brand-lockup">
            <div className="brand-mark">
              <span className="brand-lines" aria-hidden="true" />
              <span className="brand-name">CRM</span>
            </div>
            <span className="brand-chip">Account</span>
          </div>
          <p className="hero-eyebrow">Account selection</p>
        </div>

        <h1 className="hero-title">Choose an account</h1>
        <p className="hero-copy">
          Select the account you want to manage.
        </p>

      </div>

      <div className="auth-panel">
        <div className="auth-container">
          <div className="form-header">
            <div className="form-badge">AC</div>
            <div>
              <p className="form-eyebrow">Account required</p>
              <h2 className="form-title">Your accounts</h2>
              <p className="form-subtitle">
                {tenants.length
                  ? `${tenants.length} account${tenants.length === 1 ? '' : 's'} — pick one to continue.`
                  : 'Pick an account to continue.'}
              </p>
            </div>
          </div>

          {showError && <p className="error-message">{showError}</p>}

          <form onSubmit={handleSubmit}>
            <div
              className={`tenant-options${tenants.length > 4 ? ' tenant-options--scroll' : ''}`}
            >
              {tenants.map((tenant) => {
                const tenantId = String(tenant?.id || tenant?.tenant_id || '');
                return (
                  <label
                    key={tenantId || tenant.name}
                    className={`tenant-option ${selectedTenantId === tenantId ? 'selected' : ''}`}
                  >
                    <input
                      type="radio"
                      name="tenant"
                      value={tenantId}
                      checked={selectedTenantId === tenantId}
                      onChange={() => setSelectedTenantId(tenantId)}
                    />
                    <div className="tenant-option-body">
                      <div className="tenant-option-title">{tenant.name || 'Account'}</div>
                      <div className="tenant-option-meta">
                        <span>{tenant.domain || 'No domain'}</span>
                      </div>
                    </div>
                  </label>
                );
              })}

              {!tenants.length && (
                <div className="tenant-empty">
                  <p>No accounts available.</p>
                </div>
              )}
            </div>

            <button
              type="submit"
              className="login-btn"
              disabled={loading || !selectedTenantId || !tenants.length}
            >
              {loading ? 'Loading...' : 'Continue'}
            </button>

            <div className="auth-links">
              <button type="button" onClick={onLogout}>
                Sign out
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default TenantSelector;
